import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback, useMemo } from "react";
import { getAgreements } from "./agreements.thunk";
import { clearAgreements } from "./agreements.slice";
import { isError, isIdle, isLoading, isSuccess } from "@/app/utils/asyncStatus";
import useModal from "../modal/modal.hook";

const useAgreements = () => {
  const dispatch = useAppDispatch();
  const { createModal } = useModal();
  const {
    data: agreements,
    error: agreementsError,
    status: agreementsStatus,
  } = useAppSelector((store) => store.agreements);

  const fetchAgreements = useCallback(() => {
    return dispatch(getAgreements());
  }, [dispatch]);

  const clearData = useCallback(() => {
    dispatch(clearAgreements());
  }, [dispatch]);

  const requiredAgreements = useMemo(
    () => agreements?.filter((agreement) => agreement.isRequired) ?? [],
    [agreements],
  );

  const optionalAgreements = useMemo(
    () => agreements?.filter((agreement) => !agreement.isRequired) ?? [],
    [agreements],
  );

  const openAgreementModal = useCallback(
    (agreementId: number, onAccept: () => void, onReject: () => void) => {
      const agreement = agreements?.find((a) => a.id === agreementId);
      if (!agreement) return;

      createModal("consent", {
        title: agreement.title,
        type: "single",
        content: agreement.content,
        onAccept,
        onReject,
      });
    },
    [agreements, createModal],
  );

  const getAgreementById = useCallback(
    (id: number) => {
      return agreements?.find((agreement) => agreement.id === id);
    },
    [agreements],
  );

  return {
    agreements: agreements ?? [],
    requiredAgreements,
    optionalAgreements,
    agreementsError,
    agreementsStatus,
    isIdle: isIdle(agreementsStatus),
    isLoading: isLoading(agreementsStatus),
    isSuccess: isSuccess(agreementsStatus),
    isError: isError(agreementsStatus),
    fetchAgreements,
    clearData,
    openAgreementModal,
    getAgreementById,
  };
};

export default useAgreements;
